import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, merge } from 'rxjs';

import { AuthorizationCustomerActions, AuthorizationCustomerApiActions } from './customer.actions';

import { CustomerMapper } from '@modules/core/mappers';
import { CustomerApiService } from '@paymash/sdk/api/v2';
import { selectCompanyUuid } from '@public-page/store/company';
import { NotificationActions } from '@public-page/store/notification';
import { ErrorMessageKeyService } from '@modules/core/services';

@Injectable()
export class CustomerEffects {
  constructor(
    private actions$: Actions,
    private customerMapper: CustomerMapper,
    private customerApiService: CustomerApiService,
    private store: Store,
    private errorMessageKeyService: ErrorMessageKeyService
  ) {}

  loadCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationCustomerActions.load),
      concatLatestFrom(() => this.store.select(selectCompanyUuid)),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      exhaustMap(([action, companyUuid]) =>
        this.customerApiService.getCurrentCustomer(companyUuid as string).pipe(
          map((customer) => {
            const entity = this.customerMapper.mapToEntity(customer);
            return AuthorizationCustomerApiActions.loadSuccess({ customer: entity });
          }),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([AuthorizationCustomerApiActions.loadFailure(), NotificationActions.addError({ messageKey })]);
          })
        )
      )
    );
  });

  updateCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationCustomerActions.update),
      map((action) => this.customerMapper.mapToDTO(action.payload)),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid)]),
      exhaustMap(([payload, companyUuid]) =>
        this.customerApiService.updateCurrentCustomer(companyUuid as string, payload).pipe(
          map((customer) => {
            const entity = this.customerMapper.mapToEntity(customer);
            return AuthorizationCustomerApiActions.updateSuccess({ customer: entity });
          }),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([
              AuthorizationCustomerApiActions.updateFailure(),
              NotificationActions.addError({ messageKey }),
            ]);
          })
        )
      )
    );
  });
}
